/**
 * Authentication Hook
 * Provides auth state and user data
 */
import { useQuery, useMutation } from "convex/react";
import { useSession } from "@/lib/auth";
import { api } from "@jourfx/convex/client";
import { useEffect, useRef, useState } from "react";
import { featureModule } from "@jourfx/types";

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  organization: {
    id: string;
    name: string;
    slug: string;
  } | null;
  license: {
    // Legacy (v1)
    tier?: string;
    modules?: string[];
    // v2
    featureIds?: string[];
    validUntil: number;
  } | null;
  permissions: string[];
}

function mapProvisioningErrorMessage(err: unknown): string {
  const rawMessage =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";

  if (/not authorized for this application/i.test(rawMessage)) {
    return "Ihr Konto gehört zum Admin-Portal und hat keinen Zugriff auf die interne Anwendung.";
  }

  if (/no license seat assigned/i.test(rawMessage)) {
    return "Kein Lizenzplatz zugewiesen. Bitte kontaktieren Sie Ihre Administration.";
  }

  if (/no active license loaded/i.test(rawMessage) || /license is not active/i.test(rawMessage)) {
    return "Keine aktive Lizenz vorhanden. Bitte kontaktieren Sie Ihre Administration.";
  }

  if (/read-only mode due to licensing connectivity issues/i.test(rawMessage)) {
    return "Das System läuft aktuell im schreibgeschützten Modus wegen Verbindungsproblemen mit dem Lizenzserver.";
  }

  if (/system is locked due to licensing connectivity issues/i.test(rawMessage)) {
    return "Das System ist aufgrund von Verbindungsproblemen mit dem Lizenzserver gesperrt.";
  }

  if (/user not found/i.test(rawMessage)) {
    return "Kein internes Benutzerkonto gefunden. Bitte kontaktieren Sie Ihre Administration.";
  }

  return "Ihr Konto hat keinen Zugriff auf diese Anwendung. Administratoren nutzen bitte das Admin-Portal.";
}

export function useAuth() {
  const { data: session, isPending: sessionPending, error } = useSession();

  // Get extended user data from Convex by auth user ID
  // Using getByAuthId instead of me since Convex JWT auth may not be working
  const authResult = useQuery(
    api.auth.users.getByAuthIdStatus,
    session?.user ? { authUserId: session.user.id } : "skip"
  ) as
    | {
        status: "unauthenticated" | "missing_app_user";
      }
    | {
        status: "forbidden" | "denied";
        message: string;
      }
    | {
        status: "ok";
        user: any;
      }
    | undefined;

  const authStatus = authResult?.status;
  const userData = authResult && authStatus === "ok"
    ? authResult.user
    : authStatus === "missing_app_user"
      ? null
      : undefined;
  const userDataErrorMessage =
    authResult && (authStatus === "forbidden" || authStatus === "denied")
      ? authResult.message
      : null;
  const deniedByQuery = authStatus === "forbidden" || authStatus === "denied";
  const missingAppUser = authStatus === "missing_app_user";
  const awaitingConvexAuth = authStatus === "unauthenticated";

  // Provision user mutation - called when session exists but no app user
  const provisionUser = useMutation(api.auth.users.provisionFromAuth);
  const provisioningRef = useRef(false);
  const [isProvisioning, setIsProvisioning] = useState(false);

  // Track if provisioning failed (user not authorized for this app)
  const [provisioningFailed, setProvisioningFailed] = useState(false);
  const [provisioningErrorMessage, setProvisioningErrorMessage] = useState<string | null>(null);

  // Reset auth-provisioning state when session user changes
  useEffect(() => {
    provisioningRef.current = false;
    setIsProvisioning(false);
    setProvisioningFailed(false);
    setProvisioningErrorMessage(null);
  }, [session?.user?.id]);

  // Provisioning finished successfully once app user exists
  useEffect(() => {
    if (authStatus === "ok") {
      provisioningRef.current = false;
      setProvisioningFailed(false);
      setProvisioningErrorMessage(null);
    }
  }, [authStatus]);

  // Auto-provision app user if session exists but no app user record
  useEffect(() => {
    if (
      session?.user &&
      missingAppUser &&
      !provisioningRef.current &&
      !provisioningFailed
    ) {
      provisioningRef.current = true;
      setIsProvisioning(true);
      console.log("[useAuth] Provisioning app user for:", session.user.email);
      provisionUser({
        authUserId: session.user.id,
        email: session.user.email,
        name: session.user.name || session.user.email.split("@")[0],
      })
        .then((userId) => {
          console.log("[useAuth] App user provisioned:", userId);
          setIsProvisioning(false);
          setProvisioningErrorMessage(null);
        })
        .catch((err) => {
          console.error("[useAuth] Failed to provision user:", err);
          provisioningRef.current = false;
          setIsProvisioning(false);
          // SECURITY: Mark provisioning as failed - user not authorized for this app
          setProvisioningFailed(true);
          setProvisioningErrorMessage(mapProvisioningErrorMessage(err));
        });
    }
  }, [session?.user, missingAppUser, provisionUser, provisioningFailed]);

  // Keep loading while provisioning is pending to avoid redirect races.
  const isLoading =
    sessionPending ||
    (session?.user &&
      (authResult === undefined ||
        awaitingConvexAuth ||
        (missingAppUser && !provisioningFailed))) ||
    isProvisioning;

  // SECURITY: User is only authenticated if they have a valid app user record
  // If provisioning failed, they are NOT authorized for this app (e.g., admin trying to access internal app)
  const hasValidAppUser = authStatus === "ok";
  const isAuthenticated =
    !!session?.user &&
    (hasValidAppUser || isProvisioning) &&
    !provisioningFailed &&
    !deniedByQuery;

  // Build user object from session + Convex data
  // SECURITY: Only return user if they have a valid app user record or are being provisioned
  const user: User | null = session?.user && !provisioningFailed && !deniedByQuery
    ? {
        id: userData?._id?.toString() || session.user.id,
        email: userData?.email || session.user.email,
        name: userData?.name || session.user.name || "",
        role: userData?.role || "versicherter",
        status: userData?.status || "active",
        organization: userData?.organization || null,
        license: userData?.license || null,
        permissions: userData?.permissions || [],
      }
    : null;

  return {
    user,
    session,
    isLoading,
    isAuthenticated,
    error: error as Error | null,
    // SECURITY: Expose if user is not authorized for this app (e.g., admin trying to access internal app)
    notAuthorizedForApp: provisioningFailed || deniedByQuery,
    authorizationErrorMessage:
      provisioningErrorMessage ||
      (userDataErrorMessage ? mapProvisioningErrorMessage(userDataErrorMessage) : null),
  };
}

/**
 * Check if current user has a specific permission
 */
export function usePermission(permission: string): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === "super_admin") return true;
  if (user.permissions.includes("*")) return true;
  return user.permissions.includes(permission);
}

/**
 * Check if current user has a specific role
 */
export function useRole(roles: string | string[]): boolean {
  const { user } = useAuth();
  if (!user) return false;
  const roleList = Array.isArray(roles) ? roles : [roles];
  return roleList.includes(user.role);
}

/**
 * Check if organization has a specific module
 */
export function useModule(moduleId: string): boolean {
  const { user } = useAuth();
  if (!user?.license) return false;
  if (moduleId === "core") return true;

  // v2: feature-based entitlements.
  if (Array.isArray(user.license.featureIds) && user.license.featureIds.length > 0) {
    if (moduleId === "scheduling") {
      return user.license.featureIds.includes("calendar") || user.license.featureIds.includes("scheduling");
    }
    return user.license.featureIds.some((f) => featureModule(f as any) === (moduleId as any));
  }

  const tierModules: Record<string, string[]> = {
    basic: ["core"],
    professional: ["core", "epa"],
    enterprise: ["core", "epa", "ai", "reporting", "integration"],
  };

  const tierDefault = tierModules[user.license.tier ?? "basic"] || [];
  return tierDefault.includes(moduleId) || (user.license.modules ?? []).includes(moduleId);
}
